import { Indicator, IndicatorInput, IndicatorInput_InputType } from "../proto/generated/indicator"

const BASE_PRICE_SOURCES = new Set(['open', 'high', 'low', 'close'])

export const INDICATOR_PRICE_SOURCE_OPTIONS = ['open', 'high', 'low', 'close', 'hl2', 'hlc3', 'ohlc4'] as const

export const INDICATOR_OUTPUT_SOURCE_PREFIX = '@ind:'
export const INDICATOR_OUTPUT_SOURCE_STABLE_PREFIX = '@indRef:'

const STABLE_INDICATOR_OUTPUT_SOURCE_SEP = '\x1e'

export type IndicatorOutputSourceRef = {
    seriesGroupId: string
    outputCode: string
}

export type StableIndicatorOutputSourceRef = {
    indicatorCode: string
    outputCode: string
    params: Record<string, string>
}

export type IndicatorSeriesGroupLookup = Record<
    string,
    { id: string; scriptCode: string; inputValues: Record<string, string> }
>

export type IndicatorOutputSourceOption = {
    value: string
    label: string
    seriesGroupId: string
    outputCode: string
}

const INDICATOR_PRICE_SOURCE_LABELS: Record<string, string> = {
    open: 'Open',
    high: 'High',
    low: 'Low',
    close: 'Close',
    hl2: '(H+L)/2',
    hlc3: '(H+L+C)/3',
    ohlc4: '(O+H+L+C)/4',
}

export const formatIndicatorOutputSourceLabel = (indicatorShortName: string, outputTitle: string): string => {
    return `${indicatorShortName}: ${outputTitle}`
}

export const encodeIndicatorOutputSource = (ref: IndicatorOutputSourceRef): string => {
    return `${INDICATOR_OUTPUT_SOURCE_PREFIX}${ref.seriesGroupId}:${ref.outputCode}`
}

export const decodeIndicatorOutputSource = (value: string): IndicatorOutputSourceRef | null => {
    if (typeof value !== 'string' || !value.startsWith(INDICATOR_OUTPUT_SOURCE_PREFIX)) return null
    const payload = value.slice(INDICATOR_OUTPUT_SOURCE_PREFIX.length)
    const separatorIndex = payload.lastIndexOf(':')
    if (separatorIndex <= 0) return null
    return {
        seriesGroupId: payload.slice(0, separatorIndex),
        outputCode: payload.slice(separatorIndex + 1),
    }
}

export const isIndicatorOutputSource = (value: string): boolean => {
    return decodeIndicatorOutputSource(value) != null
}

export const encodeStableIndicatorOutputSource = (ref: StableIndicatorOutputSourceRef): string => {
    const paramsEncoded = encodeIndicatorInputValuesForStableRef(ref.params)
    return `${INDICATOR_OUTPUT_SOURCE_STABLE_PREFIX}${ref.indicatorCode}${STABLE_INDICATOR_OUTPUT_SOURCE_SEP}${ref.outputCode}${STABLE_INDICATOR_OUTPUT_SOURCE_SEP}${paramsEncoded}`
}

export const decodeStableIndicatorOutputSource = (value: string): StableIndicatorOutputSourceRef | null => {
    if (typeof value !== 'string' || !value.startsWith(INDICATOR_OUTPUT_SOURCE_STABLE_PREFIX)) return null
    const payload = value.slice(INDICATOR_OUTPUT_SOURCE_STABLE_PREFIX.length)
    const parts = payload.split(STABLE_INDICATOR_OUTPUT_SOURCE_SEP)
    if (parts.length !== 3) return null
    const [indicatorCode, outputCode, paramsEncoded] = parts
    if (!indicatorCode || !outputCode) return null
    return {
        indicatorCode,
        outputCode,
        params: decodeIndicatorInputValuesFromStableRef(paramsEncoded),
    }
}

export const isStableIndicatorOutputSource = (value: string): boolean => {
    return decodeStableIndicatorOutputSource(value) != null
}

export const isAnyIndicatorOutputSourceReference = (value: string): boolean => {
    return isIndicatorOutputSource(value) || isStableIndicatorOutputSource(value)
}

function encodeIndicatorInputValuesForStableRef(values: Record<string, string>): string {
    const entries = Object.entries(values ?? {}).map(([k, v]) => [sanitizeId(k), String(v ?? '')] as const)
    entries.sort((a, b) => a[0].localeCompare(b[0]))
    return entries.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&')
}

function decodeIndicatorInputValuesFromStableRef(encoded: string): Record<string, string> {
    const out: Record<string, string> = {}
    if (!encoded) return out
    for (const part of encoded.split('&')) {
        const eq = part.indexOf('=')
        if (eq <= 0) continue
        out[part.slice(0, eq)] = decodeURIComponent(part.slice(eq + 1))
    }
    return out
}

export const normalizeIndicatorInputValuesToStableRefs = (
    inputValues: Record<string, string>,
    seriesGroups: IndicatorSeriesGroupLookup,
    indicators: Indicator[],
    visiting: Set<string> = new Set()
): Record<string, string> => {
    const normalized: Record<string, string> = {}
    Object.entries(inputValues ?? {}).forEach(([key, value]) => {
        normalized[sanitizeId(key)] = resolveIndicatorSourceValueToStable(String(value ?? ''), seriesGroups, indicators, visiting)
    })
    return normalized
}

export const resolveIndicatorSourceValueToStable = (
    value: string,
    seriesGroups: IndicatorSeriesGroupLookup,
    indicators: Indicator[],
    visiting: Set<string> = new Set()
): string => {
    if (isStableIndicatorOutputSource(value)) {
        const stableRef = decodeStableIndicatorOutputSource(value)
        if (!stableRef) return value
        const params = normalizeIndicatorInputValuesToStableRefs(stableRef.params, seriesGroups, indicators, visiting)
        return encodeStableIndicatorOutputSource({
            indicatorCode: stableRef.indicatorCode,
            outputCode: stableRef.outputCode,
            params,
        })
    }

    const ref = decodeIndicatorOutputSource(value)
    if (!ref) return value
    if (visiting.has(ref.seriesGroupId)) return value

    const seriesGroup = seriesGroups[ref.seriesGroupId]
    if (!seriesGroup) return value

    visiting.add(ref.seriesGroupId)
    const params = normalizeIndicatorInputValuesToStableRefs(seriesGroup.inputValues, seriesGroups, indicators, visiting)
    visiting.delete(ref.seriesGroupId)

    return encodeStableIndicatorOutputSource({
        indicatorCode: seriesGroup.scriptCode,
        outputCode: ref.outputCode,
        params,
    })
}

export const indicatorInputValuesSemanticallyEqual = (
    alertValues: Record<string, string>,
    chartValues: Record<string, string>,
    seriesGroups?: IndicatorSeriesGroupLookup,
    indicators?: Indicator[]
): boolean => {
    const alertNorm: Record<string, string> = {}
    Object.entries(alertValues ?? {}).forEach(([k, v]) => {
        alertNorm[sanitizeId(k)] = String(v ?? '')
    })
    const chartNorm: Record<string, string> = {}
    Object.entries(chartValues ?? {}).forEach(([k, v]) => {
        chartNorm[sanitizeId(k)] = String(v ?? '')
    })

    if (seriesGroups && indicators?.length) {
        const alertStable = normalizeIndicatorInputValuesToStableRefs(alertNorm, seriesGroups, indicators)
        const chartStable = normalizeIndicatorInputValuesToStableRefs(chartNorm, seriesGroups, indicators)
        for (const key of Object.keys(alertStable)) {
            if ((alertStable[key] ?? '') !== (chartStable[key] ?? '')) return false
        }
        return true
    }

    for (const key of Object.keys(alertNorm)) {
        if ((alertNorm[key] ?? '') !== (chartNorm[key] ?? '')) return false
    }
    return true
}

export const findSeriesGroupIdForStableIndicatorOutputSource = (
    value: string,
    seriesGroups: IndicatorSeriesGroupLookup,
    indicators: Indicator[]
): string | null => {
    const stableRef = decodeStableIndicatorOutputSource(value)
    if (!stableRef) return null

    for (const [seriesGroupId, seriesGroup] of Object.entries(seriesGroups)) {
        if (seriesGroup.scriptCode !== stableRef.indicatorCode) continue
        const normalized = normalizeIndicatorInputValuesToStableRefs(seriesGroup.inputValues, seriesGroups, indicators)
        const targetParams = normalizeIndicatorInputValuesToStableRefs(stableRef.params, seriesGroups, indicators)
        if (indicatorInputValuesSemanticallyEqual(targetParams, normalized, seriesGroups, indicators)) {
            return seriesGroupId
        }
    }
    return null
}

export const formatStableIndicatorOutputSourceLabel = (
    value: string,
    indicators: Indicator[],
    depth = 0
): string => {
    if (depth > 6) return value
    const stableRef = decodeStableIndicatorOutputSource(value)
    if (!stableRef) return value

    const indicator = indicators.find((item) => item.code === stableRef.indicatorCode)
    if (!indicator) {
        return `${stableRef.indicatorCode}: ${stableRef.outputCode}`
    }

    const output = indicator.outputs.find((item) => item.code === stableRef.outputCode)
    const shortName = indicator.shortName || indicator.code
    const outputTitle = output?.title ?? stableRef.outputCode
    const producerLabel = formatIndicatorOutputSourceLabel(shortName, outputTitle)
    const sourceInput = getIndicatorSourceInputValue(stableRef.params, indicator.inputs)
    if (!sourceInput || !isAnyIndicatorOutputSourceReference(sourceInput)) {
        return producerLabel
    }
    return `${producerLabel} (${formatIndicatorSourceValueForDisplay(sourceInput, indicators, depth + 1)})`
}

export const formatIndicatorSourceValueForDisplay = (
    value: string,
    indicators: Indicator[],
    depth = 0
): string => {
    if (isStableIndicatorOutputSource(value)) {
        return formatStableIndicatorOutputSourceLabel(value, indicators, depth)
    }
    if (isIndicatorOutputSource(value)) {
        return 'Indicator output'
    }
    return getIndicatorPriceSourceLabel(value)
}

export const getIndicatorPriceSourceLabel = (value: string): string => {
    return INDICATOR_PRICE_SOURCE_LABELS[value.toLowerCase()] ?? value
}

export const getIndicatorSourceLabel = (
    value: string,
    indicatorSourceLabels?: Record<string, string>,
    indicators?: Indicator[]
): string => {
    if (indicatorSourceLabels?.[value]) {
        return indicatorSourceLabels[value]
    }
    if (isStableIndicatorOutputSource(value) && indicators?.length) {
        return formatStableIndicatorOutputSourceLabel(value, indicators)
    }
    if (isIndicatorOutputSource(value)) {
        return value
    }
    return getIndicatorPriceSourceLabel(value)
}

const getInputOptionsLength = (input: IndicatorInput): number => input.options?.length ?? 0

export const isIndicatorPriceSourceInput = (input: IndicatorInput): boolean => {
    if (input.type === IndicatorInput_InputType.SOURCE) {
        return true
    }
    return getInputOptionsLength(input) > 0 && input.options!.every((option) => BASE_PRICE_SOURCES.has(option.toLowerCase()))
}

export const isIndicatorSelectInput = (input: IndicatorInput): boolean => {
    return isIndicatorPriceSourceInput(input) ||
        (input.type === IndicatorInput_InputType.STRING && getInputOptionsLength(input) > 0)
}

export const getIndicatorInputSelectOptions = (input: IndicatorInput): string[] => {
    if (isIndicatorPriceSourceInput(input)) {
        return [...INDICATOR_PRICE_SOURCE_OPTIONS]
    }
    return input.options
}

export const resolveIndicatorInputValue = (
    inputValues: Record<string, string>,
    data: { code?: string; title?: string; defval?: unknown }
): string | undefined => {
    const keys = [
        data.code,
        data.title,
        data.code ? sanitizeId(data.code) : undefined,
        data.title ? sanitizeId(data.title) : undefined,
    ].filter(Boolean) as string[]

    for (const key of keys) {
        const value = inputValues[key]
        if (value !== undefined && value !== '') {
            return value
        }
    }
    return undefined
}

export const getIndicatorSourceInputValue = (
    inputValues: Record<string, string>,
    inputs: IndicatorInput[] | undefined
): string | undefined => {
    if (!inputs?.length) return undefined
    for (const input of inputs) {
        if (!isIndicatorPriceSourceInput(input)) continue
        const value = inputValues[input.code]
        if (value) return value
    }
    return undefined
}

const getConfiguredIndicatorSourceDependencies = (
    seriesGroupId: string,
    seriesGroups: Record<string, { id: string; scriptCode: string; inputValues: Record<string, string> }>,
    indicators: Indicator[]
): string[] => {
    const seriesGroup = seriesGroups[seriesGroupId]
    if (!seriesGroup) return []

    const indicator = indicators.find((item) => item.code === seriesGroup.scriptCode)
    if (!indicator) return []

    const dependencies = new Set<string>()
    indicator.inputs.forEach((input) => {
        if (!isIndicatorPriceSourceInput(input)) return
        const value = seriesGroup.inputValues[input.code]
        const ref = value ? decodeIndicatorOutputSource(value) : null
        if (ref && ref.seriesGroupId !== seriesGroupId) {
            dependencies.add(ref.seriesGroupId)
        }
    })
    return Array.from(dependencies)
}

export const getIndicatorSourceDependents = (
    producerSeriesGroupId: string,
    seriesGroups: Record<string, { id: string; scriptCode: string; inputValues: Record<string, string> }>,
    indicators: Indicator[]
): string[] => {
    const indicatorSeriesGroupIds = Object.values(seriesGroups)
        .filter((group) => indicators.some((indicator) => indicator.code === group.scriptCode))
        .map((group) => group.id)

    const dependents = new Set<string>()

    const collectDependents = (producerId: string) => {
        indicatorSeriesGroupIds.forEach((consumerId) => {
            if (consumerId === producerId || dependents.has(consumerId)) return
            const dependencies = getConfiguredIndicatorSourceDependencies(consumerId, seriesGroups, indicators)
            if (dependencies.includes(producerId)) {
                dependents.add(consumerId)
                collectDependents(consumerId)
            }
        })
    }

    collectDependents(producerSeriesGroupId)
    return Array.from(dependents)
}

export type IndicatorSourceDependentDisplay = {
    seriesGroupId: string
    name: string
    shortName: string
}

export const getIndicatorSourceDependentDisplays = (
    producerSeriesGroupId: string,
    seriesGroups: Record<string, { id: string; scriptCode: string; inputValues: Record<string, string> }>,
    indicators: Indicator[]
): IndicatorSourceDependentDisplay[] => {
    return getIndicatorSourceDependents(producerSeriesGroupId, seriesGroups, indicators).map((seriesGroupId) => {
        const group = seriesGroups[seriesGroupId]
        const indicator = indicators.find((item) => item.code === group?.scriptCode)
        const name = indicator?.name || (group as { name?: string })?.name || seriesGroupId
        const shortName = indicator?.shortName || indicator?.code || name
        return { seriesGroupId, name, shortName }
    })
}

export const getIndicatorSourceDependentDisplayNames = (
    producerSeriesGroupId: string,
    seriesGroups: Record<string, { id: string; scriptCode: string; inputValues: Record<string, string> }>,
    indicators: Indicator[]
): string[] => {
    return getIndicatorSourceDependentDisplays(producerSeriesGroupId, seriesGroups, indicators).map(
        ({ name, shortName }) => (shortName && name !== shortName ? `${name} (${shortName})` : name)
    )
}

export const wouldCreateIndicatorSourceCycle = (
    consumerSeriesGroupId: string,
    producerSeriesGroupId: string,
    seriesGroups: Record<string, { id: string; scriptCode: string; inputValues: Record<string, string> }>,
    indicators: Indicator[]
): boolean => {
    if (consumerSeriesGroupId === producerSeriesGroupId) {
        return true
    }

    const indicatorIds = Object.values(seriesGroups)
        .filter((group) => indicators.some((indicator) => indicator.code === group.scriptCode))
        .map((group) => group.id)

    const dependsOn = (fromId: string, targetId: string, visited: Set<string>): boolean => {
        if (fromId === targetId) return true
        if (visited.has(fromId)) return false
        visited.add(fromId)

        for (const dependencyId of getConfiguredIndicatorSourceDependencies(fromId, seriesGroups, indicators)) {
            if (!indicatorIds.includes(dependencyId)) continue
            if (dependsOn(dependencyId, targetId, visited)) {
                return true
            }
        }
        return false
    }

    return dependsOn(producerSeriesGroupId, consumerSeriesGroupId, new Set())
}

export const sortIndicatorSeriesGroupsForEvaluation = (
    seriesGroupIds: string[],
    seriesGroups: Record<string, { id: string; scriptCode: string; inputValues: Record<string, string> }>,
    indicators: Indicator[]
): string[] => {
    const sorted: string[] = []
    const visited = new Set<string>()
    const visiting = new Set<string>()

    const visit = (seriesGroupId: string) => {
        if (visited.has(seriesGroupId)) return
        if (visiting.has(seriesGroupId)) return

        visiting.add(seriesGroupId)
        getConfiguredIndicatorSourceDependencies(seriesGroupId, seriesGroups, indicators).forEach((dependencyId) => {
            if (seriesGroupIds.includes(dependencyId)) {
                visit(dependencyId)
            }
        })
        visiting.delete(seriesGroupId)
        visited.add(seriesGroupId)
        sorted.push(seriesGroupId)
    }

    seriesGroupIds.forEach(visit)
    return sorted
}

export const getIndicatorDisplayName = (
    indicator: Indicator,
    config: Record<string, string>,
    resolveSourceLabel?: (value: string) => string
): string => {
    let name = indicator.shortName || indicator.code

    if (indicator.inputs.length) {
        name += ' ('
        const inputs = indicator.inputs
            .map((input) => {
                const rawValue = config[input.code]
                if (rawValue === undefined || rawValue === null || rawValue === '') return null
                const value = String(rawValue)
                if (resolveSourceLabel && isAnyIndicatorOutputSourceReference(value)) {
                    return resolveSourceLabel(value)
                }
                return value
            })
            .filter(Boolean)

        name += `${inputs.join(', ')})`
    }

    return name
}

export const getIndicatorDisplayNameForAlert = (
    indicator: Indicator,
    config: Record<string, string>,
    indicators: Indicator[],
    resolveSourceLabel?: (value: string) => string
): string => {
    return getIndicatorDisplayName(
        indicator,
        config,
        resolveSourceLabel ??
            ((value) => formatIndicatorSourceValueForDisplay(value, indicators))
    )
}

export const sanitizeId = (param: string) => param.replace(/\W+/g, "_").toUpperCase()
